import { containsProfanity } from "./profanity"

export type NameValidationError =
  | "INVALID_NAME"
  | "NAME_TOO_LONG"
  | "NAME_RESERVED"
  | "PROFANITY"

export type NameValidationResult =
  | {
      ok: true
      displayName: string
      normalized: string
    }
  | {
      ok: false
      reason: NameValidationError
    }

const RESERVED_NAMES = new Set([
  "host",
  "admin",
  "system",
  "imposter",
  "bot",
  "cpu",
  "you",
  "server",
  "moderator",
  "owner",
])

const ALLOWED_NAME_PATTERN = /^[\p{L}\p{N} _'-]+$/u

// Zero-width and bidi-override characters used in homoglyph/spoofing attacks.
// Stripped before any length or charset check so a name composed only of these
// fails as INVALID_NAME (empty after strip), not silently passes as "non-empty".
const ZERO_WIDTH_PATTERN = /[\u200B-\u200D\u2066-\u2069\u202A-\u202E\uFEFF]/g

const MAX_NAME_CODEPOINTS = 20

function preNormalize(raw: string): string {
  return raw.normalize("NFC").replace(ZERO_WIDTH_PATTERN, "")
}

export function normalizePlayerName(value: string) {
  return preNormalize(value)
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase()
}

export function validatePlayerName(value: string): NameValidationResult {
  if (typeof value !== "string") {
    return { ok: false, reason: "INVALID_NAME" }
  }

  const displayName = preNormalize(value).trim()

  if (displayName.length === 0) {
    return { ok: false, reason: "INVALID_NAME" }
  }

  // Count Unicode code points, not UTF-16 code units. Astral characters
  // (e.g. some CJK ideographs above U+FFFF) count as 1.
  const codepointCount = Array.from(displayName).length
  if (codepointCount > MAX_NAME_CODEPOINTS) {
    return { ok: false, reason: "NAME_TOO_LONG" }
  }

  if (!ALLOWED_NAME_PATTERN.test(displayName)) {
    return { ok: false, reason: "INVALID_NAME" }
  }

  const normalized = displayName.replace(/\s+/g, " ").toLocaleLowerCase()

  if (RESERVED_NAMES.has(normalized)) {
    return { ok: false, reason: "NAME_RESERVED" }
  }

  if (containsProfanity(displayName)) {
    return { ok: false, reason: "PROFANITY" }
  }

  return { ok: true, displayName, normalized }
}
